// Indicador PASSIVO do fetch de boot do catálogo. Substitui o
// `BandwidthProbeOverlay` (cortina full-screen, z-index 9999, com botão
// "Pular" focável que roubava o foco da Home por 1,6-2,8s na LG Chr53).
//
// Contrato comportamental (desenho do ux-tv, não estético):
// - NÃO é focável. Zero `Focusable`, zero `tabIndex`, zero `setFocus`. Barra de
//   progresso é STATUS, não CONTROLE — focável no-op é anti-padrão da casa
//   (mesma regra dos dots do slider em `onboarding-entrada-tv-ux.md` §2).
// - NÃO bloqueia. Sem backdrop, sem `inset: 0`. A Home fica 100% navegável
//   enquanto o catálogo baixa (`api.ts` é local-first com rede como FALLBACK —
//   a Home funciona sem o snapshot; ele é otimização, não caminho crítico).
// - NÃO empurra layout: `position: fixed` no canto, fora do fluxo. Nada de
//   reflow que mova o card focado.
// - Em vlow/low NÃO renderiza barra: sem `onProgress` nesses tiers a barra era
//   FALSA (VLOW_BAR_PCT cravado 15/50/95/100). Barra determinada mentirosa
//   justo no aparelho mais lento é a pior combinação possível — vira só rótulo.
//
// A tarefa (download + medição de banda) vive em `../boot-snapshot`. Aqui só se
// LÊ estado. Esconder este componente não cancela download nenhum.
//
// `CatalogSyncIndicatorView` é exportado separado (props puras) pra o gate
// visual/i18n conseguir montar as 2 fases nos 3 locales sem rede.

import { useTranslation } from '@app-iptv/i18n'
import { useEffect, useRef, useState } from 'react'

import { useBootSnapshotBytes, useBootSnapshotPhase } from '../boot-snapshot'

import { pickPlacement, type Box } from './catalog-sync-placement'
import styles from './CatalogSyncIndicator.module.css'

/** Estimativa de tamanho típico do snapshot (~5MB) pra derivar o percentual.
 *  Pode subestimar em provider grande — daí o cap em 90% antes do fim (evita
 *  "100% e ainda baixando"). */
const ESTIMATED_SNAPSHOT_BYTES = 5_000_000

/** Fases com pixel. As demais (`idle`/`checking`/`done`) não renderizam nada. */
export type SyncStep = 'connecting' | 'downloading'

interface ViewProps {
  step: SyncStep
  /** Bytes baixados. Sempre 0 em vlow/low (não há `onProgress` lá). */
  bytes: number
  /** vlow/low: sem barra (ver cabeçalho). */
  lowTier: boolean
}

/** Âncora + se está cedendo a vez. Um estado só (string) pra o bail-out por
 *  Object.is do `useState` matar o re-render quando nada muda — o medidor roda
 *  várias vezes por segundo enquanto a pílula existe. */
type Slot = 'bottom' | 'top' | 'bottom-yield' | 'top-yield'

/** Re-medição periódica enquanto a pílula existe. NÃO é polling de foco (isso
 *  quem faz é o MutationObserver, que dispara na hora): é pra pegar o REPOUSO —
 *  o card focado entra na posição final por uma transição de 200-300ms
 *  (orçamento do ux-tv), então o rect lido no instante da troca de foco ainda é
 *  o de ANTES do scroll. 200ms cobre a transição inteira em no máximo uma
 *  passada extra. Custo total: a pílula vive 1,6-2,8s no boot, ou seja ~12
 *  leituras de rect de UM elemento na vida inteira do componente. */
const SETTLE_POLL_MS = 200

function readFocusRects(): Box[] {
  const out: Box[] = []
  // querySelectorAll, não querySelector: `[data-focused]` não é garantidamente
  // único (Focusable aninhado) — o próprio helper do gate de foco lê a lista.
  const nodes = document.querySelectorAll<HTMLElement>('[data-focused]')
  for (let i = 0; i < nodes.length; i += 1) {
    const r = nodes[i]!.getBoundingClientRect()
    // Elemento sem caixa (display:none em transição) não disputa espaço.
    if (r.width <= 0 || r.height <= 0) continue
    out.push({ left: r.left, right: r.right, top: r.top, bottom: r.bottom })
  }
  return out
}

/**
 * A pílula CEDE a vez pro foco (ressalva R2 do ux-tv). Detalhes do porquê e da
 * escolha da âncora estão em `catalog-sync-placement.ts`; aqui só o encanamento.
 *
 * O gatilho é o INVARIANTE ("o retângulo focado encosta na faixa?"), nunca uma
 * PROXY do tipo "o usuário apertou pra baixo" — `MutationObserver` em
 * `data-focused` pega TODOS os caminhos (D-pad vertical e horizontal, BACK,
 * `setFocus` programático, zap, remonte de tela) porque observa a consequência,
 * não a causa. `resize` e o repouso do scroll entram pelo timer.
 *
 * `useEffect` (passivo) de propósito, NÃO `useLayoutEffect`: o Norigin registra
 * e pinta `[data-focused]` nos effects PASSIVOS dele, então um layout effect
 * leria o DOM antes do foco existir e mediria estado STALE (a armadilha do rc21).
 */
function useYieldToFocus(): { ref: React.RefObject<HTMLDivElement>; slot: Slot } {
  const ref = useRef<HTMLDivElement>(null)
  const [slot, setSlot] = useState<Slot>('bottom')
  const slotRef = useRef<Slot>('bottom')
  slotRef.current = slot

  useEffect(() => {
    let raf = 0

    const measure = (): void => {
      raf = 0
      const el = ref.current
      if (!el) return
      const anchor: 'bottom' | 'top' = slotRef.current.indexOf('top') === 0 ? 'top' : 'bottom'
      // A pílula é medida SEMPRE — mesmo cedendo ela mantém caixa
      // (`visibility: hidden`, não `display: none`). Sem caixa a próxima medição
      // leria 0x0, escolheria 'bottom' e o par ceder/voltar oscilaria.
      const r = el.getBoundingClientRect()
      if (r.height <= 0) return
      const vh = window.innerHeight
      // O inset sai do rect MEDIDO, não do token: vale igual na TV (safe area) e
      // no web (onde --safe-* é 0), e sobrevive a qualquer mudança de token sem
      // precisar ler custom property — que no `.ipk` achatado devolve vazio.
      const insetY = anchor === 'top' ? r.top : vh - r.bottom
      const picked = pickPlacement({
        focusRects: readFocusRects(),
        chipLeft: r.left,
        chipRight: r.right,
        chipHeight: r.height,
        insetY,
        viewportHeight: vh,
      })
      const next: Slot =
        picked === 'hidden' ? (anchor === 'top' ? 'top-yield' : 'bottom-yield') : picked
      if (next !== slotRef.current) setSlot(next)
    }

    /** Coalesce: o Norigin troca `data-focused` em 2 mutações (sai de um, entra
     *  no outro) e as duas cairiam aqui no mesmo tick. */
    const schedule = (): void => {
      if (raf !== 0) return
      raf = requestAnimationFrame(measure)
    }

    schedule()
    const mo = new MutationObserver(schedule)
    mo.observe(document.body, {
      attributes: true,
      subtree: true,
      attributeFilter: ['data-focused'],
    })
    const timer = window.setInterval(schedule, SETTLE_POLL_MS)
    window.addEventListener('resize', schedule)

    return () => {
      if (raf !== 0) cancelAnimationFrame(raf)
      mo.disconnect()
      window.clearInterval(timer)
      window.removeEventListener('resize', schedule)
    }
  }, [])

  return { ref, slot }
}

const SLOT_CLASS: Record<Slot, string> = {
  bottom: styles.atBottom ?? '',
  top: styles.atTop ?? '',
  'bottom-yield': `${styles.atBottom ?? ''} ${styles.yielded ?? ''}`,
  'top-yield': `${styles.atTop ?? ''} ${styles.yielded ?? ''}`,
}

export function CatalogSyncIndicatorView({ step, bytes, lowTier }: ViewProps) {
  const { t } = useTranslation()
  const { ref, slot } = useYieldToFocus()
  // Cap em 90%: o denominador é estimativa, não Content-Length (o body vem
  // comprimido; o total do `onProgress` não é confiável).
  const raw = (bytes / ESTIMATED_SNAPSHOT_BYTES) * 90
  const pct = raw > 90 ? 90 : raw < 0 ? 0 : raw

  return (
    <div
      ref={ref}
      className={`${styles.chip} ${SLOT_CLASS[slot]}`}
      data-slot={slot}
      role="status"
      aria-live="polite"
    >
      <span className={styles.dot} aria-hidden="true" />
      <span className={styles.label}>{t(`bandwidth.step.${step}`)}</span>
      {!lowTier && (
        <span className={styles.bar} aria-hidden="true">
          <span className={styles.barFill} style={{ width: `${pct}%` }} />
        </span>
      )}
    </div>
  )
}

export function CatalogSyncIndicator() {
  const phase = useBootSnapshotPhase()
  const bytes = useBootSnapshotBytes()

  if (phase !== 'connecting' && phase !== 'downloading') return null

  // Tier lido do <html> (mesma fonte do overlay antigo). Não muda durante o
  // boot — override de tier em Configurações remonta a árvore.
  const tier =
    (typeof document !== 'undefined' ? document.documentElement.dataset['tier'] : null) ??
    'mid'
  const lowTier = tier === 'vlow' || tier === 'low'

  return <CatalogSyncIndicatorView step={phase} bytes={bytes} lowTier={lowTier} />
}
